import { useState, useEffect, useCallback } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiRawFetch } from '../../main/client';
import { formatDateForLocale } from '../../core/dateFormatting';
import type { TaxPeriod } from '../hooks/useTaxPeriods';
import type { CreatePaymentData, TaxPayment } from '../hooks/useTaxPayments';

const CURRENCIES = ['HUF', 'EUR', 'USD', 'GBP', 'CHF'];

const dialogStyles = {
    '& .MuiDialog-paper': {
        borderRadius: 0,
        border: '3px solid #6366f1',
        backgroundColor: '#0f0f23',
        minWidth: 400,
    },
};

const inputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: '#1a1a2e',
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    },
};

interface RecordPaymentDialogProps {
    open: boolean;
    onClose: () => void;
    period: TaxPeriod | null;
    onSave: (data: CreatePaymentData) => Promise<void>;
    onDelete: (paymentId: number) => Promise<void>;
    propertyId: number | undefined;
}

const getComponentDisplayName = (componentId: string): string => {
    const names: Record<string, string> = {
        'tourism-tax': 'Tourism Tax',
        'tourism-dev': 'Tourism Dev. Contribution',
        'szja': 'SZJA (Income Tax)',
        'szocho': 'SZOCHO (Social Contribution)',
        'tb': 'TB (Health Contribution)',
        'ipa': 'IPA (Local Business Tax)',
    };
    return names[componentId] ?? componentId.toUpperCase();
};

const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const RecordPaymentDialog = ({ open, onClose, period, onSave, onDelete, propertyId }: RecordPaymentDialogProps) => {
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('');
    const [paidDate, setPaidDate] = useState('');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [payments, setPayments] = useState<TaxPayment[]>([]);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

    const fetchPayments = useCallback(async () => {
        if (!propertyId || !period) {
            setPayments([]);
            return;
        }
        const response = await apiRawFetch(`api/properties/${propertyId}/tax-payments?taxPeriodId=${period.id}`);
        if (response.ok) {
            const { data } = await response.json();
            setPayments(data);
        }
    }, [propertyId, period]);

    useEffect(() => {
        if (open && period) {
            fetchPayments();
        }
    }, [open, period, fetchPayments]);

    useEffect(() => {
        if (period) {
            const remaining = period.targetAmount != null ? period.targetAmount - period.totalPaid : null;
            setAmount(remaining != null && remaining > 0 ? remaining.toString() : '');
            setCurrency(period.currency);
            setPaidDate(new Date().toISOString().split('T')[0]);
            setReference('');
            setNotes('');
        }
    }, [period]);

    const handleSave = async () => {
        if (!period) return;

        setSaving(true);
        try {
            await onSave({
                taxPeriodId: period.id,
                amount: parseFloat(amount),
                currency,
                paidDate,
                reference: reference || undefined,
                notes: notes || undefined,
            });
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (paymentId: number) => {
        await onDelete(paymentId);
        setConfirmDeleteId(null);
        await fetchPayments();
    };

    const isValid = amount && parseFloat(amount) > 0 && currency && paidDate;

    return (
        <Dialog open={open} onClose={onClose} sx={dialogStyles}>
            <DialogTitle
                sx={{
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '3px solid #374151',
                    pb: 2,
                }}
            >
                Record Payment
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                {period && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        {getComponentDisplayName(period.componentId)} - {period.periodStart.slice(0, 7)}
                    </Typography>
                )}

                {payments.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        >
                            Recorded Payments
                        </Typography>
                        <Stack spacing={1}>
                            {payments.map((payment) => (
                                <Box
                                    key={payment.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1.5,
                                        border: '2px solid #374151',
                                        backgroundColor: '#1a1a2e',
                                    }}
                                >
                                    {confirmDeleteId === payment.id ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>
                                                Delete this payment?
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => handleDelete(payment.id)}
                                                sx={{
                                                    color: '#ef4444',
                                                    fontWeight: 700,
                                                    fontSize: '0.7rem',
                                                    textTransform: 'uppercase',
                                                    minWidth: 'auto',
                                                }}
                                            >
                                                Yes
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={() => setConfirmDeleteId(null)}
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.7rem',
                                                    textTransform: 'uppercase',
                                                    minWidth: 'auto',
                                                }}
                                            >
                                                No
                                            </Button>
                                        </Box>
                                    ) : (
                                        <>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                                                >
                                                    {formatCurrency(Number(payment.amount), payment.currency)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDateForLocale(payment.paidDate)}
                                                    {payment.reference ? ` · ${payment.reference}` : ''}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => setConfirmDeleteId(payment.id)}
                                                sx={{ color: '#6b7280', '&:hover': { color: '#ef4444' } }}
                                            >
                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                <Stack spacing={3}>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Amount"
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            fullWidth
                            required
                            inputProps={{ min: 0, step: 1 }}
                            sx={inputStyles}
                        />
                        <FormControl sx={{ ...inputStyles, minWidth: 100 }}>
                            <InputLabel id="currency-label">Currency</InputLabel>
                            <Select
                                labelId="currency-label"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                label="Currency"
                            >
                                {CURRENCIES.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <TextField
                        label="Payment Date"
                        type="date"
                        value={paidDate}
                        onChange={(e) => setPaidDate(e.target.value)}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={inputStyles}
                    />

                    <TextField
                        label="Reference Number"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        fullWidth
                        placeholder="e.g., NAV-2025-001"
                        sx={inputStyles}
                    />

                    <TextField
                        label="Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        sx={inputStyles}
                    />
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 2,
                    borderTop: '3px solid #374151',
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        borderRadius: 0,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isValid || saving}
                    sx={{
                        borderRadius: 0,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        backgroundColor: '#10b981',
                        '&:hover': {
                            backgroundColor: '#059669',
                        },
                    }}
                >
                    {saving ? 'Saving...' : 'Save Payment'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RecordPaymentDialog;
